import { useState, useCallback } from 'react';
import emailService from '../utils/emailService';

export const useEmail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quota, setQuota] = useState(null);

  // Initialize email service
  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const success = await emailService.initialize();
      if (success) {
        // Check quota after initialization
        const quotaResult = await emailService.checkQuota();
        if (quotaResult.success) {
          setQuota(quotaResult);
        }
      }
      return success;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Send welcome email
  const sendWelcomeEmail = useCallback(async (userEmail, userName) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await emailService.sendWelcomeEmail(userEmail, userName);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Send password reset email
  const sendPasswordResetEmail = useCallback(async (userEmail, resetToken) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await emailService.sendPasswordResetEmail(userEmail, resetToken);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Send email verification
  const sendEmailVerification = useCallback(async (userEmail, verificationToken) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await emailService.sendEmailVerification(userEmail, verificationToken);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Send notification email
  const sendNotificationEmail = useCallback(async (userEmail, title, message, actionLink, actionText) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await emailService.sendNotificationEmail(userEmail, title, message, actionLink, actionText);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Send custom email
  const sendEmail = useCallback(async (to, subject, body, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await emailService.sendEmail(to, subject, body, options);
      if (!result.success) {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Check quota
  const checkQuota = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await emailService.checkQuota();
      if (result.success) {
        setQuota(result);
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    quota,
    initialize,
    sendWelcomeEmail,
    sendPasswordResetEmail,
    sendEmailVerification,
    sendNotificationEmail,
    sendEmail,
    checkQuota,
    clearError
  };
};

